import React, { useState, useEffect, useRef } from 'react';
import { useDispatch } from 'react-redux';
import { updatePORequest } from '../../store/slices/poRequestSlice';
import { useToast } from '../../contexts/ToastContext';
import DateInput from './DateInput';
import { getCurrentDateForInput, getDatePlaceholder as getMomentDatePlaceholder } from '../../utils/momentDateFormatter';
import type { AppDispatch } from '../../store';

interface ProjectEditViewModalProps {
  projectData?: ProjectData;
  onCancel?: () => void;
  onSave?: () => void;
  deliveryDate?: string;
  onDeliveryDateChange?: (date: string) => void;
}

interface ProjectData {
  id: string;
  requestedDate: string;
  quantity: number;
  itemId?: number;
  itemName?: string;
  itemCode?: string;
  shortCode?: string;
  categoryId?: number;
  categoryName?: string;
  attachmentUrl?: string;
  isUrgent?: boolean;
  requestStatus?: string;
  approvedQuantity?: number;
}

const ProjectEditViewModal: React.FC<ProjectEditViewModalProps> = ({
  projectData: initialProjectDataModal,
  onCancel,
  onSave,
  deliveryDate,
  onDeliveryDateChange
}) => {
  const dispatch = useDispatch<AppDispatch>();
  const { showSuccess, showError, showWarning } = useToast();
  const quantityInputRef = useRef<HTMLInputElement>(null);
  const [formData, setFormData] = useState<ProjectData>(initialProjectDataModal || {
    id: '',
    requestedDate: '',
    quantity: 1
  });

  // Update form data when initial data changes
  useEffect(() => {
    if (initialProjectDataModal) {
      setFormData(initialProjectDataModal);
    }
  }, [initialProjectDataModal]);

  // Modal açıldığında miktar alanına focus ver
  useEffect(() => {
    if (quantityInputRef.current) {
      // Kısa bir gecikme ile focus ver (modal animasyonu için)
      setTimeout(() => {
        quantityInputRef.current?.focus();
      }, 100);
    }
  }, [initialProjectDataModal]);

  // Endpoint çağrıları kaldırıldı - gerekli veriler parent'tan gelecek

  const handleInputChange = (field: keyof ProjectData, value: string | number) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleCancel = () => {
    if (onCancel) {
      onCancel();
    }
  };

  const handleSave = async () => {
    // Check if quantity is valid
    if (formData.quantity <= 0) {
      showWarning('Miktar 0\'dan büyük olmalıdır!');
      return;
    }

    // Check if delivery date is in the past
    if (formData.requestedDate) {
      const today = new Date();
      const todayString = today.toISOString().split('T')[0];
      if (todayString && formData.requestedDate < todayString) {
        showWarning('İstenilen teslim tarihi geçmiş bir tarih olamaz!');
        return;
      }
    }
    
    try {
      // Create updated PORequest data - same structure as TalepOlustur edit modal
      const requestData = {
        id: parseInt(formData.id) || 0,
        itemId: formData.itemId || 0,
        itemCode: formData.itemCode || null,
        shortCode: formData.shortCode || null,
        itemName: formData.itemName || null,
        categoryId: formData.categoryId || 0,
        categoryName: formData.categoryName || null,
        attachmentUrl: formData.attachmentUrl || null,
        requestedQuantity: formData.quantity,
        requestedDeliveryDate: new Date(deliveryDate || formData.requestedDate),
        isUrgent: formData.isUrgent || false,
        requestStatus: formData.requestStatus || null,
        approvedQuantity: formData.approvedQuantity || 0
      };

      // Call the API to update PORequest
      const result = await dispatch(updatePORequest({ id: parseInt(formData.id), requestData })).unwrap();
      
      // The response is now wrapped in { id, response }
      if (result.response && typeof result.response === 'object' && 'success' in result.response) {
        if (result.response.success) {
          showSuccess('Talep başarıyla güncellendi!');
        } else {
          showError(result.response.message || 'Talep güncellenirken hata oluştu');
        }
      } else {
        showSuccess('Talep başarıyla güncellendi!');
      }
      
      // Call onSave callback if provided
      if (onSave) {
        onSave();
      }
      
      // Close modal
      if (onCancel) {
        onCancel();
      }
      
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Bilinmeyen bir hata oluştu';
      showError(`Talep güncellenirken hata oluştu: ${errorMessage}`);
    }
  };


  return (
    <div className="w-full p-6">
      {/* Close Button */}
      <button 
        onClick={handleCancel}
        className="absolute top-4 right-4 z-10 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 p-2 rounded-lg transition-colors"
        title="Kapat"
      >
        <i className="fas fa-times text-xl"></i>
      </button>
      
      {/* Header */}
      <div className="mb-6">
        <h3 className="text-xl font-bold text-gray-900 dark:text-white">Talep Düzenle</h3>
        <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">Talep No: #{formData.id || '-'}</p>
        {formData.itemName && (
          <p className="text-sm text-gray-600 dark:text-gray-300 mt-1">
            <span className="font-medium">Ürün:</span> {formData.itemName} ({formData.itemCode})
          </p>
        )}
      </div>

      {/* Form */}
      <div className="space-y-6">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              Miktar
            </label>
            <input
              ref={quantityInputRef}
              type="number"
              value={formData.quantity}
              onChange={(e) => {
                const value = e.target.value;
                if (value === '') {
                  handleInputChange('quantity', '');
                } else {
                  const numValue = parseFloat(value);
                  handleInputChange('quantity', isNaN(numValue) ? 0 : numValue);
                }
              }}
              onFocus={(e) => {
                setTimeout(() => {
                  e.target.select();
                }, 0);
              }}
              min="1"
              className="w-full px-4 py-3 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-600 focus:border-transparent bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
              placeholder="Miktar giriniz"
            />
          </div>

          <div>
            <DateInput
              label="İstenilen Teslim Tarihi"
              value={deliveryDate || formData.requestedDate}
              onChange={onDeliveryDateChange || ((value) => handleInputChange('requestedDate', value))}
              min={getCurrentDateForInput()}
              placeholder={getMomentDatePlaceholder()}
              required={true}
              className="w-full"
            />
          </div>
        </div>
      </div>

      {/* Footer with Save and Cancel buttons */}
      <div className="flex justify-end space-x-3 pt-6 mt-6 border-t border-gray-200 dark:border-gray-600">
        <button
          onClick={handleCancel}
          className="px-6 py-2 text-gray-700 dark:text-gray-300 bg-gray-100 dark:bg-gray-700 rounded-lg hover:bg-gray-200 dark:hover:bg-gray-600 transition-colors font-medium"
        >
          İptal
        </button>
        <button
          onClick={handleSave}
          className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors font-medium"
        >
          <i className="fas fa-save mr-2"></i>
          Kaydet
        </button>
      </div>
    </div>
  );
};

export default ProjectEditViewModal;